import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Moon, Sun } from "lucide-react";

export type Theme = "system" | "light" | "dark";

const KEY = "theme";
const next: Record<Theme, Theme> = {
  system: "light",
  light: "dark",
  dark: "system",
};
const icons = { system: Monitor, light: Sun, dark: Moon };
const listeners = new Set<() => void>();
// Guarded because jsdom has no matchMedia.
const osDark =
  typeof matchMedia === "function"
    ? matchMedia("(prefers-color-scheme: dark)")
    : undefined;

function read(): Theme {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === "light" || saved === "dark" ? saved : "system";
  } catch {
    return "system";
  }
}

// Same rule as public/theme.js, which applies it before first paint.
function apply() {
  const theme = read();
  document.documentElement.classList.toggle(
    "dark",
    theme === "dark" || (theme === "system" && osDark?.matches === true),
  );
  listeners.forEach((listener) => listener());
}

osDark?.addEventListener("change", apply);
if (typeof window !== "undefined")
  window.addEventListener("storage", (event) => event.key === KEY && apply());

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** The current choice, and a step to the next one: automatisch, licht, donker. */
export function useTheme() {
  const { t } = useTranslation();
  const theme = useSyncExternalStore(subscribe, read);

  return {
    Icon: icons[theme],
    label: t("theme.label", { theme: t(`theme.${theme}`) }),
    cycle: () => {
      try {
        if (next[theme] === "system") localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, next[theme]);
      } catch {
        // Storage off: the choice cannot be kept, so the OS setting stays in charge.
      }
      apply();
    },
  };
}
